import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useSignInFlow } from '../hooks/useSignInFlow'
import { EMAIL_FLOWS } from '../customerBuild'
import { RithmoWordmark } from './shell/OrgMark'
import { productBrand as brand } from './shell/brand'
import { TONE_CLASS } from './shell/agentTones'

/**
 * The customer build's sign-in page: the Control Board's sign-in layout and words, on Rithmo's own sign-in flow (useSignInFlow): the server's error
 * message, the return to the page that sent here, invitations and unverified accounts work exactly as in the normal
 * dashboard. The two notices the Control Board has no equivalent for (session expired, password reset) use its alert
 * box with its own warn / ok tones; signup and reset links appear only where this deployment can send email. Shared sign-in
 * (Google / Microsoft / company SSO) is offered exactly when the server enables it, as on the normal sign-in page.
 */
const SSO_LABEL = { google: 'Google', microsoft: 'Microsoft' }

export default function CustomerLoginPage() {
  const { signIn, loading: busy, error, ssoError, ssoMethods, companyAvailable, startCompanySso, startGlobalSso } = useSignInFlow()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const passwordReset = EMAIL_FLOWS && searchParams.get('reset') === 'success'
  const sessionExpired = searchParams.get('expired') === 'true'

  const submit = (e) => {
    e.preventDefault()
    signIn(email.trim(), password)
  }

  return (
    <div className="grid min-h-screen bg-gray-50 lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-gradient p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <RithmoWordmark size={36} onDark />
        </div>
        <div className="max-w-md">
          <h2 className="text-3xl font-bold leading-tight">{brand.signInHeadline}</h2>
          <p className="mt-4 text-base leading-relaxed text-white/75">
            {brand.signInLede}
          </p>
        </div>
        <p className="text-sm text-white/60">{brand.governedBy}</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <RithmoWordmark size={36} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{brand.signInTitle}</h1>
          <p className="mt-2 text-sm text-gray-500">{brand.signInSubtitle}</p>

          {error && <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          {!error && ssoError && <div role="alert" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{ssoError}</div>}
          {!error && !ssoError && passwordReset && (
            <div role="status" className={`mt-6 rounded-xl border p-3 text-sm ${TONE_CLASS.ok}`}>Password reset successfully. Sign in with your new password.</div>
          )}
          {!error && !ssoError && !passwordReset && sessionExpired && (
            <div role="status" className={`mt-6 rounded-xl border p-3 text-sm ${TONE_CLASS.warn}`}>Your session has expired. Please sign in again.</div>
          )}

          <label htmlFor="email" className="mt-6 block text-sm font-semibold text-gray-900">Email</label>
          <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-400" />

          <label htmlFor="password" className="mt-4 block text-sm font-semibold text-gray-900">Password</label>
          <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-400" />

          <button type="submit" disabled={busy}
            className="mt-6 w-full rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {/* Shared sign-in: Google / Microsoft and company SSO, shown only when the server reports them enabled. */}
          {(ssoMethods.length > 0 || companyAvailable) && (
            <div className="mt-6">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-300" />
                <span className="text-xs text-gray-600">or</span>
                <div className="h-px flex-1 bg-gray-300" />
              </div>
              <div className="mt-4 space-y-3">
                {ssoMethods.map((m) => (
                  <button key={m} type="button" onClick={() => startGlobalSso(m)}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                    Continue with {SSO_LABEL[m]}
                  </button>
                ))}
                {companyAvailable && (
                  <button type="button" onClick={startCompanySso}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                    Continue with company SSO
                  </button>
                )}
              </div>
            </div>
          )}

          {EMAIL_FLOWS && (
            <p className="mt-6 text-center text-sm text-gray-500">
              <Link to="/forgot-password" className="font-semibold text-primary-700 hover:text-primary-800">Forgot password?</Link>
              {' · '}
              <Link to="/signup" className="font-semibold text-primary-700 hover:text-primary-800">Create an account</Link>
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
